import React, { useMemo, useState } from "react";

import { CAMPAIGN_TEMPLATE } from "../../../../types/templates/campaign-brief/campaign-brief-template.enum";
import { Tabs } from "../../../../components/layout/tabs/components/tabs/tabs.component";
import { UnsavedChangesModal } from "../../../../components/modals/UnsavedChangesModal/unsaved-changes-modal.component";
import { isNavigatingAwayFrom } from "../../../../components/layout/tabs/components/tabs/tabs.functions";

import { getCbTemplateViewFromType } from "../../features/cb-template/types/cb-template-view.functions";

import { CampaignBriefApprovalsTab } from "../campaign-brief-approvals-tab/campaign-brief-approvals-tab.component";
import { CampaignBriefCommentsTab } from "../campaign-brief-comments-tab/campaign-brief-comments-tab.component";
import { CampaignBriefOverviewTab } from "../campaign-brief-overview-tab/campaign-brief-overview-tab.component";
import { CampaignBriefProductsTab } from "../campaign-brief-products-tab/campaign-brief-products-tab.component";
import { ExecutionBriefTab } from "../campaign-brief-execution-briefs-tab/campaign-brief-execution-briefs-tab.component";

import { CampaignBriefFormProps } from "./campaign-brief-form.props";

export const CampaignBriefFormComponent = (props: CampaignBriefFormProps) => {
  const { forceOpen = false, headerHeight = 0, isDirty = false, template } = props;
  const { isUnderNda, underNdaUserDetails } = props.ndaData;

  const Template = useMemo(() => getCbTemplateViewFromType(template as CAMPAIGN_TEMPLATE), [
    template,
  ]);

  const [forceOpenOnProductsTab, setForceOpenOnProductsTab] = useState<false | (() => any)>(false);
  const [productsFormIsDirty, setProductsFormIsDirty] = useState<boolean>(false);

  const shouldChangeProductTab = (newTabValue: number, changeTab: () => void) => {
    if (productsFormIsDirty && isNavigatingAwayFrom(1, props.currentTabValue, newTabValue)) {
      setForceOpenOnProductsTab(() => (response: boolean) => {
        setForceOpenOnProductsTab(false);

        if (response) {
          setProductsFormIsDirty(false);
          changeTab();
        }
      });

      return false;
    }

    return props.shouldChangeTab(newTabValue, changeTab);
  };

  const when = (): boolean => productsFormIsDirty && !forceOpenOnProductsTab;
  const tabPageTitles = [
    "Campaign Overview",
    "Products",
    "Approvals",
    "Comments",
    "Multi Channel Briefs",
  ];

  const tabs: { title: string; contents: Element | JSX.Element }[] = [
    {
      title: tabPageTitles[0],
      contents: (
        <CampaignBriefOverviewTab
          pageTitle={tabPageTitles[0]}
          ndaData={props.ndaData}
          attachedFiles={props.attachedFiles}
          isDirty={isDirty}
          form={props.form}
          onDeleteFile={props.onDeleteFile}
          onUploadFile={props.onUploadFile}
          templateView={Template}
          forceOpen={forceOpen}
          userPermissions={props.userPermissions}
        />
      ),
    },
    {
      title: tabPageTitles[1],
      contents: (
        <>
          <UnsavedChangesModal forceOpen={forceOpenOnProductsTab} when={when()} />
          <CampaignBriefProductsTab
            endDate={props.defaultValues.endAt}
            briefId={props.resourceId}
            pageTitle={tabPageTitles[1]}
            setProductsFormIsDirty={setProductsFormIsDirty}
          />
        </>
      ),
    },
    {
      title: tabPageTitles[2],
      contents: <CampaignBriefApprovalsTab status={props.status} pageTitle={tabPageTitles[2]} />,
    },
    {
      title: tabPageTitles[3],
      contents: <CampaignBriefCommentsTab pageTitle={tabPageTitles[3]} />,
    },
    {
      title: tabPageTitles[4],
      contents: <ExecutionBriefTab pageTitle={tabPageTitles[4]} />,
    },
  ];

  return (
    <Tabs
      currentTabValue={props.currentTabValue}
      shouldChangeTab={shouldChangeProductTab}
      handleTabChange={props.handleTabChange}
      headerHeight={headerHeight}
      isUnderNda={isUnderNda}
      tabs={tabs}
      underNdaUserDetails={underNdaUserDetails}
    />
  );
};
